import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { DollarSign, Wrench, FileClock, Users } from "lucide-react";
import api, { currency } from "@/lib/api";

const STATUS_COLORS = {
  Estimate: "text-zinc-300 bg-zinc-700/40",
  Approved: "text-green-400 bg-green-500/15",
  "In Progress": "text-yellow-400 bg-yellow-500/15",
  Completed: "text-blue-400 bg-blue-500/15",
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/dashboard/stats").then(({ data }) => setStats(data)); }, []);
  if (!stats) return <p className="text-zinc-500 font-display uppercase">Loading…</p>;

  const kpis = [
    { label: "Revenue (paid)", value: currency(stats.total_revenue), icon: DollarSign, accent: true },
    { label: "Outstanding", value: currency(stats.outstanding), icon: FileClock },
    { label: "Active Builds", value: stats.active_builds, icon: Wrench },
    { label: "Customers", value: stats.total_customers, icon: Users },
  ];
  const pipeline = ["Estimate", "Approved", "In Progress", "Completed"];

  return (
    <div>
      <h1 className="font-display text-3xl font-black uppercase tracking-tighter mb-1">Dashboard</h1>
      <p className="text-zinc-500 text-sm mb-8">Operations overview</p>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {kpis.map((k, i) => (
          <motion.div key={k.label} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
            className={`border p-6 ${k.accent ? "border-yellow-500/50 bg-yellow-500/5" : "border-zinc-800 bg-zinc-950"}`} data-testid={`kpi-${i}`}>
            <k.icon className={k.accent ? "text-yellow-500" : "text-zinc-500"} size={22} />
            <p className="text-xs uppercase tracking-wide text-zinc-500 mt-4">{k.label}</p>
            <p className={`font-display text-3xl font-black mt-1 ${k.accent ? "text-yellow-500" : "text-white"}`}>{k.value}</p>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 border border-zinc-800 bg-zinc-950 p-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-6">Revenue · Last 6 months</p>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={stats.revenue_chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
              <XAxis dataKey="month" stroke="#71717a" fontSize={12} />
              <YAxis stroke="#71717a" fontSize={12} tickFormatter={(v) => `$${v}`} />
              <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #27272a", color: "#fff" }} formatter={(v) => currency(v)} cursor={{ fill: "#ffffff08" }} />
              <Bar dataKey="revenue" fill="#EAB308" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="border border-zinc-800 bg-zinc-950 p-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-6">Build Pipeline</p>
          <div className="space-y-4">
            {pipeline.map((s) => (
              <div key={s} className="flex items-center justify-between" data-testid={`pipeline-${s.replace(" ", "-")}`}>
                <span className={`text-xs font-bold uppercase px-3 py-1 ${STATUS_COLORS[s]}`}>{s}</span>
                <span className="font-display text-2xl font-black">{stats.pipeline[s] || 0}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="border border-zinc-800 bg-zinc-950 p-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4">Recent Builds</p>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-zinc-500 uppercase text-xs">
              <th className="py-2">Build</th><th>Customer</th><th>Vehicle</th><th className="text-right">Total</th><th className="text-right">Status</th>
            </tr>
          </thead>
          <tbody>
            {stats.recent_builds.map((b) => (
              <tr key={b.id} className="border-t border-zinc-800 even:bg-zinc-900/40" data-testid={`recent-${b.build_number}`}>
                <td className="py-3 font-semibold">{b.build_number}</td>
                <td>{b.customer_name}</td>
                <td className="text-zinc-400">{b.vehicle.make} {b.vehicle.model}</td>
                <td className="text-right">{currency(b.approved_total || b.subtotal)}</td>
                <td className="text-right"><span className={`text-xs font-bold uppercase px-2 py-1 ${STATUS_COLORS[b.status]}`}>{b.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
